import './WithdrawalRejectController.scss';

import { Button } from '@modulor/react';
import { useSkeletonTranslation } from 'hooks/useSkeletonTranslation';
import { useSetHeaderConfig } from 'Modulor/components/Header/hooks/useSetHeaderConfig';
import { useNavigateWithSearch } from 'Modulor/hooks';
import type { FC } from 'react';
import { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { getClickStreamEvent, getEventAnalytic, useGetClickStreamCashierContext } from 'services/clickstream';
import { FlowTypes } from 'store/constants';
import { useAppSelector } from 'store/hooks';
import { WithdrawalRejectRequestStatus } from 'store/models';
import { useLazyWithdrawalRejectRequestQuery } from 'store/services/withdrawalRejectAPI';
import { resetAutoSelectTriggered } from 'store/slices/autoFlow';
import { currencyDisplaySelector } from 'store/slices/deposit';
import {
  isPartialReverseEnabledSelector,
  pendingWithdrawalsSelector,
  setCloseTrigger,
  setWithdrawalRejectRequestResponse,
  startWithdrawalRejectRequestFetching,
  stopWithdrawalRejectRequestFetching,
  withdrawalRejectInfoSelector,
  withdrawalRejectRequestResponseSelector,
} from 'store/slices/withdrawalReject';
import { getFormattedAmountWithCurrency } from 'utils';

import { Routes } from '../../routes';
import { PartialReverseContainer } from './PartialReverse/PartialReverseContainer';
import { ReverseHeader } from './ReverseHeader';
import {
  getReversePopupType,
  isWithdrawalRejectBilletClosedCookieSet,
  setWithdrawalRejectBilletClosedCookies,
} from './utils';

const clickstream = getEventAnalytic();

export const WithdrawalRejectController: FC = () => {
  const dispatch = useDispatch();
  const { translate, skeletonT } = useSkeletonTranslation();
  const navigate = useNavigateWithSearch();
  const onCloseLocal = () =>
    navigate(`/${Routes.deposit}`, {
      replace: true,
    });
  useSetHeaderConfig({
    preset: 'rejectWithdrawal',
  });
  const cashierContext = useGetClickStreamCashierContext(FlowTypes.withdrawal);
  const withdrawalRejectInfo = useAppSelector(withdrawalRejectInfoSelector);
  const withdrawalRejectInfoRef = useRef(withdrawalRejectInfo);

  const withdrawalRejectRequestResponse = useAppSelector(withdrawalRejectRequestResponseSelector);
  const [trigger, result] = useLazyWithdrawalRejectRequestQuery();
  const { data, isError } = result;
  const currencyDisplaySetting = useAppSelector(currencyDisplaySelector);
  const pendingWithdrawals = useAppSelector(pendingWithdrawalsSelector);
  const isPartialReverseEnabled = useAppSelector(isPartialReverseEnabledSelector);

  const handleClickDeposit = useCallback(() => {
    setWithdrawalRejectBilletClosedCookies();
    dispatch(setCloseTrigger());
    dispatch(resetAutoSelectTriggered());
    onCloseLocal();
  }, [dispatch, navigate]);

  const onCloseHandler = useCallback(
    (isCrossClicked?: boolean) => {
      const event = getClickStreamEvent.cashier_homepage_popup_cancel_withdrawal_not_confirm_button_click;
      clickstream.push(event, [cashierContext]);
      if (isCrossClicked && !withdrawalRejectRequestResponse) {
        setWithdrawalRejectBilletClosedCookies();
      }

      dispatch(setCloseTrigger());
      dispatch(resetAutoSelectTriggered());
      onCloseLocal();
    },
    [withdrawalRejectRequestResponse],
  );

  useEffect(() => {
    if (isWithdrawalRejectBilletClosedCookieSet()) {
      dispatch(setCloseTrigger());
      onCloseLocal();
      return undefined;
    }

    return () => {
      dispatch(setCloseTrigger());
    };
  }, []);

  useEffect(() => {
    if (!withdrawalRejectRequestResponse) {
      return;
    }
    setTimeout(onCloseHandler, 40000);
  }, [withdrawalRejectRequestResponse]);

  const onSuccessHandler = () => {
    const event = getClickStreamEvent.cashier_homepage_popup_cancel_withdrawal_confirm_button_click;
    clickstream.push(event, [cashierContext]);
    dispatch(startWithdrawalRejectRequestFetching());
    trigger();
  };

  useEffect(() => {
    if (isError) {
      dispatch(stopWithdrawalRejectRequestFetching());
      dispatch(setCloseTrigger());
      navigate(Routes.depositError);
    }

    if (!data) {
      return;
    }

    const { status } = data as unknown as {
      status?: WithdrawalRejectRequestStatus;
    };

    const path = status === WithdrawalRejectRequestStatus.success ? Routes.depositSuccess : Routes.depositError;

    dispatch(setWithdrawalRejectRequestResponse(data));
    dispatch(stopWithdrawalRejectRequestFetching());
    dispatch(setCloseTrigger());

    navigate(path);
  }, [data, isError]);

  // frozen on open: closing clears the store data, the popup must not switch type underneath the player
  const isPartialReverseRef = useRef(
    getReversePopupType({
      withdrawalReverseAmount: withdrawalRejectInfo?.amount,
      pendingWithdrawals,
      isPartialReverseEnabled,
    }) === 'partial',
  );

  return (
    <div className="withdrawal-reject">
      <div className="content">
        {isPartialReverseRef.current ? (
          <PartialReverseContainer onDeposit={handleClickDeposit} />
        ) : (
          <>
            <ReverseHeader
              title={translate('PH.DEPOSIT.WITHDRAWAL_REVERCE.TITLE')}
              subtitle={translate('PH.DEPOSIT.WITHDRAWAL_REVERCE.BODY')}
            />
            <div className="buttons">
              <Button variant="deposit" onClick={onSuccessHandler}>
                {skeletonT('PH.BUTTON.WITHDRAWAL_REVERCE', {
                  amount: getFormattedAmountWithCurrency(
                    `${withdrawalRejectInfoRef.current?.amount}`,
                    withdrawalRejectInfoRef.current?.currency,
                    currencyDisplaySetting,
                  ),
                })}
              </Button>
              <Button variant="secondary" onClick={handleClickDeposit}>
                {skeletonT('CASHIER.DEPOSIT')}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
